import moment from 'moment';
import type { AssistantAudience } from './assistantCapabilities';
import {
  buyerGroupOf,
  invoicePageLink,
  invoiceTypeLabel,
  isRefund,
  statusLabelFor,
  supplierGroupOf,
} from './invoiceStatus';
import type { KnowledgeAnswer, KnowledgeBreakdownRow, KnowledgeContext, KnowledgeQuery } from './knowledgeRegistry';
import { displayDate, displayDateRange, isForbidden, money } from './lookupFormat';
import { claimsLookup, extractLabelledBookingNumber } from './lookupRouting';
import { findInvoicesForBooking, type InvoiceLookupRow } from './requests/invoice-lookup-requests';

/**
 * Lists the invoices on one booking — "what has booking 1021445 been charged",
 * "has booking 1021445 been paid out", "show me the invoices for booking …".
 *
 * A booking's money is spread over many invoices: the first charge, each
 * recurring period, overages, date adjustments, refunds. Customers ask about the
 * booking, not the invoices, so this collects them, says how many stand where,
 * calls out any that need attention, and links to the invoice page filtered to
 * the booking. Each row links to its own invoice, and "look up invoice N" gives
 * the full detail of one.
 */

/** Invoices listed under the answer; the rest are one click away. */
const LISTED = 12;

const SLASH_DAY = 'MM/DD/YYYY';

const BARE_NUMBER = /\b(\d{5,})\b/;

const bookingNumberIn = (message: string): string | null =>
  extractLabelledBookingNumber(message) ?? message.match(BARE_NUMBER)?.[1] ?? null;

/** Status groups that mean something has gone wrong and someone should act. */
const ATTENTION: Record<AssistantAudience, Set<string>> = {
  buyer: new Set(['PAYMENT_FAILED', 'DECLINED', 'NEEDS_CUSTOMER_APPROVAL']),
  supplier: new Set(['PAYMENT_FAILED', 'PAYOUT_FAILED', 'PAYOUT_ON_HOLD', 'CHARGE_ON_HOLD', 'DECLINED']),
  admin: new Set([
    'PAYMENT_FAILED',
    'DECLINED',
    'CHARGE_ON_HOLD',
    'SECURSPACE_PAYOUT_FAILED',
    'SECURSPACE_PAYOUT_CANCELED',
    'SUPPLIER_PAYOUT_ON_HOLD',
    'SUPPLIER_PAYOUT_FAILED',
    'SUPPLIER_PAYOUT_FAILED_SECURSPACE_FEE_PAYOUT_SUCCEEDED',
    'SUPPLIER_PAYOUT_SUCCEEDED_SECURSPACE_FEE_PAYOUT_FAILED',
    'REFUND_REQUESTED',
  ]),
};

const attentionKey = (row: InvoiceLookupRow, audience: AssistantAudience): string => {
  if (audience === 'buyer') return buyerGroupOf(row);
  if (audience === 'supplier') return supplierGroupOf(row);
  return row.status ?? '';
};

const byNumberDesc = (a: InvoiceLookupRow, b: InvoiceLookupRow): number =>
  Number(b.transactionNumber ?? 0) - Number(a.transactionNumber ?? 0);

/** "5 Complete, 1 Payout Pending and 6 Charge Pending", largest first. */
const statusTally = (rows: InvoiceLookupRow[], audience: AssistantAudience): string => {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const label = statusLabelFor(row, audience) || 'Unknown';
    counts.set(label, (counts.get(label) ?? 0) + 1);
  });
  const parts = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => `${count} ${label}`);
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

const sum = (rows: InvoiceLookupRow[], pick: (row: InvoiceLookupRow) => number | undefined): number =>
  rows.reduce((total, row) => total + (pick(row) ?? 0), 0);

/** The money that has actually moved, in the terms the audience cares about. */
const settledSentence = (rows: InvoiceLookupRow[], audience: AssistantAudience): string => {
  const charges = rows.filter((row) => !isRefund(row));
  const refunds = rows.filter(isRefund);
  const paid = charges.filter((row) => buyerGroupOf(row) === 'PAYMENT_SUCCEEDED');
  const paidOut = charges.filter((row) => supplierGroupOf(row) === 'PAYOUT_SUCCEEDED');
  const refunded = refunds.filter((row) => buyerGroupOf(row) === 'PAYMENT_SUCCEEDED');
  const refundPhrase = refunded.length
    ? ` ${money(sum(refunded, (row) => row.buyerAmount))} has been refunded.`
    : '';

  if (audience === 'buyer') {
    return `${money(sum(paid, (row) => row.buyerAmount))} has been paid so far.${refundPhrase}`;
  }
  if (audience === 'supplier') {
    return `${money(sum(paidOut, (row) => row.supplierAmount))} has been paid out to you so far.`;
  }
  return (
    `The buyer has paid ${money(sum(paid, (row) => row.buyerAmount))} and the supplier has been paid ` +
    `out ${money(sum(paidOut, (row) => row.supplierAmount))}.${refundPhrase}`
  );
};

const rowFor = (row: InvoiceLookupRow, audience: AssistantAudience): KnowledgeBreakdownRow => {
  const number = String(row.transactionNumber ?? '');
  const dates = displayDateRange(row.serviceStartDate ?? row.startDate, row.serviceEndDate ?? row.endDate);
  const amount = money(audience === 'supplier' ? row.supplierAmount : row.buyerAmount);
  return {
    label: [`#${number}`, invoiceTypeLabel(row), dates].filter(Boolean).join(' \u00b7 '),
    value: [amount, statusLabelFor(row, audience)].filter(Boolean).join(' \u00b7 '),
    link: invoicePageLink(audience, { invoiceNumber: number }, { from: row.createdOn, to: row.createdOn }),
  };
};

export const answerBookingInvoices = async ({ message, audience }: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const bookingNumber = bookingNumberIn(message);
  if (!bookingNumber) {
    return {
      text:
        'Which booking do you mean? Tell me its booking number \u2014 for example _"show the ' +
        'invoices for booking 1021445"_ \u2014 and I will list its invoices and where each stands.',
    };
  }

  let found: Awaited<ReturnType<typeof findInvoicesForBooking>>;
  try {
    found = await findInvoicesForBooking(audience, bookingNumber);
  } catch (err) {
    if (isForbidden(err)) {
      return {
        text:
          'Your user role cannot view invoices, so I cannot list them for you. An account owner ' +
          'can, or SecurSpace support can help.',
      };
    }
    throw err;
  }

  const rows = [...found.rows].sort(byNumberDesc);
  if (rows.length === 0) {
    return {
      text:
        `I could not find any invoices for booking **#${bookingNumber}**` +
        `${audience === 'admin' ? '' : ' on your account'}. Double-check the booking number; a ` +
        'booking that was never completed or approved may have no invoices yet.',
    };
  }

  const count = `${rows.length}${found.truncated ? '+' : ''}`;
  const noun = rows.length === 1 && !found.truncated ? 'invoice' : 'invoices';
  const attention = rows.filter((row) => ATTENTION[audience].has(attentionKey(row, audience)));
  const attentionSentence = attention.length
    ? ` Needs attention: ${attention
        .slice(0, 5)
        .map((row) => `**#${row.transactionNumber}** (${statusLabelFor(row, audience)})`)
        .join(', ')}.`
    : '';
  const truncatedSentence = found.truncated
    ? ` I have counted the most recent ${rows.length}; open the invoice page for the rest.`
    : '';
  const created = rows
    .map((row) => moment(displayDate(row.createdOn), SLASH_DAY, true))
    .filter((date) => date.isValid());
  const oldest = created.length > 0 ? moment.min(created).format(SLASH_DAY) : undefined;
  const newest = created.length > 0 ? moment.max(created).format(SLASH_DAY) : undefined;

  return {
    text:
      `Booking **#${bookingNumber}** has **${count} ${noun}**: ${statusTally(rows, audience)}. ` +
      `${settledSentence(rows, audience)}${attentionSentence}${truncatedSentence}` +
      (rows.length > LISTED ? ` The latest ${LISTED} are below.` : ''),
    breakdown: rows.slice(0, LISTED).map((row) => rowFor(row, audience)),
    link: invoicePageLink(audience, { bookingNumber }, { from: found.firstCreatedOn ?? oldest, to: newest }),
  };
};

export const bookingInvoicesQuery: KnowledgeQuery = {
  id: 'bookingInvoices',
  audiences: ['admin', 'supplier', 'buyer'],
  synonyms: [
    'invoices for a booking',
    'invoices on a booking',
    'invoices for this booking',
    'charges on a booking',
    'charges for a booking',
    'billing history for a booking',
    'payment history for a booking',
    'what has a booking been charged',
  ],
  claims: claimsLookup('bookingInvoices'),
  needsLocations: false,
  example: 'show the invoices for booking 1021445',
  description:
    'Lists every invoice on one booking, looked up by its booking number (e.g. "show the ' +
    'invoices for booking 1021445", "has booking 1021445 been paid out", "what was charged on ' +
    'booking 1021445"): how many stand in each status, what has been paid, paid out or refunded, ' +
    'and which need attention (failed payments, held or failed payouts), with a link per invoice. ' +
    'About one named booking\u2019s money, not a count of invoices over a period.',
  answer: answerBookingInvoices,
};

export const __testing = { bookingNumberIn, statusTally, settledSentence };
